import type { CSSProperties, ElementType, FC } from 'react'

import { Fragment } from 'react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'

interface SplitTextProps {
    lines: Array<string>
    as?: ElementType
    className?: string
    lineClassName?: string
    delay?: number
    highlight?: string
    highlightDelay?: number
    /** One word rendered in the serif italic for contrast. */
    italic?: string
    id?: string
}

export const SERIF_WORD = 'font-serif font-normal italic tracking-[-0.01em]'

const Underline: FC<{ delay: number }> = ({ delay }) => {
    return (
        <svg
            aria-hidden='true'
            viewBox='0 0 300 28'
            fill='none'
            className='pointer-events-none absolute left-[-5%] top-[86%] h-auto w-[110%] overflow-visible text-ember'
        >
            <path
                className='draw-path'
                pathLength={1}
                d='M5 17C61 8 124 4 172 7C219 10 255 15 295 9'
                stroke='currentColor'
                strokeWidth='8'
                strokeLinecap='round'
                style={{ '--draw-delay': `${delay}ms` } as CSSProperties}
            />
            <path
                className='draw-path'
                pathLength={1}
                d='M38 23C104 17 178 16 268 20'
                stroke='currentColor'
                strokeWidth='4.5'
                strokeLinecap='round'
                style={{ '--draw-delay': `${delay + 380}ms` } as CSSProperties}
            />
        </svg>
    )
}

export const SplitText: FC<SplitTextProps> = ({
    lines,
    as: Tag = 'h2',
    className,
    lineClassName,
    delay = 0,
    highlight,
    highlightDelay = 950,
    italic,
    id
}) => {
    const { ref, inView } = useInView<HTMLElement>({ rootMargin: '0px 0px -6% 0px' })

    let index = 0

    return (
        <Tag
            ref={ref}
            id={id}
            data-split=''
            data-revealed={inView ? '' : undefined}
            className={className}
            style={{ '--delay': `${delay}ms` } as CSSProperties}
        >
            <span className='sr-only'>
                {lines.join(' ')}
            </span>

            {lines.map((line, lineIndex) => {
                const words = line.split(' ')

                return (
                    <span
                        key={`${line}-${lineIndex}`}
                        aria-hidden='true'
                        className={cn('split-line', lineClassName)}
                    >
                        {words.map((word, wordIndex) => {
                            const order = index++
                            const bare = word.replace(/[^\p{L}\p{N}']/gu, '').toLowerCase()
                            const isHighlight = Boolean(highlight) && bare === highlight?.toLowerCase()
                            const isItalic = Boolean(italic) && bare === italic?.toLowerCase()

                            const masked = (
                                <span className='split-mask'>
                                    <span
                                        className={cn('split-inner', isItalic && SERIF_WORD)}
                                        style={{ '--i': order } as CSSProperties}
                                    >
                                        {word}
                                    </span>
                                </span>
                            )

                            return (
                                <Fragment key={`${word}-${wordIndex}`}>
                                    {isHighlight ? (
                                        <span className='relative inline-block'>
                                            {masked}
                                            <Underline delay={highlightDelay} />
                                        </span>
                                    ) : masked}
                                    {wordIndex < words.length - 1 ? ' ' : null}
                                </Fragment>
                            )
                        })}
                    </span>
                )
            })}
        </Tag>
    )
}
